import React, { useState } from "react";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import ProductCardActions from "./ProductCardActions";
import type { TransformedProduct } from "../../types/productApiTypes";
import { getProductImageSource } from "../../helpers/productImgHelpers";
import { useTranslation } from "react-i18next"; // Import the hook

// Define props interface for ProductCard component
interface ProductCardProps {
  product: TransformedProduct;
  viewMode?: "grid" | "list";
}

// Define props for the skeleton component
interface ProductCardSkeletonProps {
  viewMode?: "grid" | "list";
}

const ProductCard: React.FC<ProductCardProps> = ({
  product,
  viewMode = "grid",
}) => {
  const { t } = useTranslation();

  // State to track if image is loaded
  const [imageLoaded, setImageLoaded] = useState<boolean>(false);

  // Get the product image source using the helper
  const imageSource = getProductImageSource(product);

  // Handle image load complete
  const handleImageLoad = (): void => {
    setImageLoaded(true);
  };

  // Get the primary category
  const primaryCategory = product.enrichedCategories?.[0];

  return (
    <>
      <div
        className={`product-card product-card-${viewMode}`}
        data-product-id={product.id}
      >
        <a href={product.permalink} className="product-link">
          <div
            className={`product-image ${
              imageLoaded ? "image-loaded" : "image-loading"
            }`}
          >
            {/* Add hidden placeholder to maintain space */}
            <div className="image-placeholder"></div>

            <img
              src={imageSource}
              alt={product.name}
              onLoad={handleImageLoad}
              loading="lazy" // Add native lazy loading
              className={imageLoaded ? "visible" : ""}
            />
            {/* REMOVED Sale and Out of Stock badges */}
          </div>

          <div className="product-content">
            {/* ADDED: Primary category display */}
            {primaryCategory && (
              <span className="product-category">{primaryCategory.name}</span>
            )}
            <h2 className="product-title">{product.name}</h2>

            {/* REMOVED: Price display block */}

            {/* Show short description in list view */}
            {viewMode === "list" && product.shortDescription && (
              <div
                className="product-short-description"
                dangerouslySetInnerHTML={{ __html: product.shortDescription }}
              />
            )}
          </div>
        </a>

        {/* Render the simplified actions component */}
        <ProductCardActions product={product} />
      </div>
    </>
  );
};

// Create a skeleton version of the ProductCard
const ProductCardSkeleton: React.FC<ProductCardSkeletonProps> = ({
  viewMode = "grid",
}) => {
  return (
    <div
      className={`product-card product-card-skeleton product-card-${viewMode}`}
    >
      <div className="product-link">
        <div className="product-image">
          <Skeleton height="100%" width="100%" />
        </div>
        <div className="product-content">
          {/* ADDED: Skeleton for category */}
          <span className="product-category">
            <Skeleton height={16} width="50%" />
          </span>
          <h2 className="product-title">
            <Skeleton height={22} width="80%" />
          </h2>
          {/* REMOVED: Skeleton for price */}
        </div>
      </div>

      <div className="product-card-actions">
        <Skeleton height={36} width="100%" />
      </div>
    </div>
  );
};

// Define the type for the component with the Skeleton property
type ProductCardComponent = React.FC<ProductCardProps> & {
  Skeleton: React.FC<ProductCardSkeletonProps>;
};

// Cast the component to include the Skeleton property
(ProductCard as ProductCardComponent).Skeleton = ProductCardSkeleton;

// Export the component with its proper type
export default ProductCard as ProductCardComponent;
